import EventRestrictions from "@/components/molecules/headers/details-page/badges/EventRestrictions";
import GoogleMapContainer from "@/components/molecules/maps/GoogleMapContainer";
import { isStringWithContent } from "@/helpers/text-utils";
import Accommodations from "@app-contexts/public/features/event-detail/components/Accommodations/Accommodations.web";
import CustomerReviews from "@app-contexts/public/features/event-detail/components/CustomerReviews/CustomerReviews.web";
import { useCustomerReviewsData } from "@app-contexts/public/features/event-detail/hooks/useCustomerReviewsData";
import useDetailPageScrollMargin from "@app-contexts/public/features/event-detail/hooks/useDetailPageScrollMargin";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import HelpAndInfoModalSelecton from "@app/core/src/shared/modules/seating/components/helpAndInfoModal/HelpAndInfoModalSelection";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import ErrorBoundary from "@shared/components/application/other/ErrorBoundary/ErrorBoundary.web";
import { enumsTypes } from "@shared/constants";
import useNativeAppCheck from "@shared/hooks/platform/useNativeAppCheck";
import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import ParsedHtml from "@ui-kit/typography/ParsedHtml/ParsedHtml.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useRouter } from "next/router";
import React from "react";
import { PublicRatingSerializer } from "@app/core/src/shared/modules/events/types/public";
import { CUSTOMER_REVIEWS } from "@app/core/src/shared/constants/event-detail";
import useDjangoWaffle from "@app/core/src/shared/hooks/djangoWaffle/useDjangoWaffle";
import UpcomingEventsSlider from "./events/UpcomingEventsSlider";

export interface DetailPageInfoProps {
  infoHeader: string;
  infoText: string;
  infoRestrictions?: string[];
  refundPolicyText: string;
  isLoading: boolean;
  isAttractionPage: boolean;
  openContactDetailsModal?: () => void;
  venueInfo: {
    venueId: number | undefined;
    venueSlug: string | undefined;
    venueDescription: string | undefined;
    venueName: string | undefined;
    venuePrivate: boolean | undefined;
    venueRating?: PublicRatingSerializer;
    eventRating?: PublicRatingSerializer;
  };
  eventInfo?: {
    eventId: number;
    eventLocationName: string;
    eventAddress: string;
    eventLatLng: string;
    eventVirtualType: enumsTypes.VirtualEventMapIdType;
    eventAccommodationUrl?: string;
    viewMap: boolean;
    handleViewMap: (input: boolean) => void;
  };
}

const DetailPageInfo: React.FC<DetailPageInfoProps> = ({
  // user,
  infoHeader,
  infoText,
  infoRestrictions = [],
  openContactDetailsModal = () => {
    /* empty */
  },
  refundPolicyText,
  isLoading,
  isAttractionPage,
  venueInfo,
  eventInfo = undefined,
}) => {
  const { t } = useTranslation("purchases");
  const router = useRouter();
  const { isReactNativeApp } = useNativeAppCheck();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { usePublicUpcomingEvents } = usePublicVenueService();
  const { isFlagActive } = useDjangoWaffle();

  const upcomingEventsPageSize = 9;

  // Get a venue's upcoming events and display them on the purchase page
  // Do not display this component on attraction pages
  const { upcomingEvents } = usePublicUpcomingEvents(
    venueInfo.venueSlug || "",
    {
      page_size: upcomingEventsPageSize,
      exclude_events: eventInfo?.eventId.toString(),
    },
    { enabled: !isAttractionPage }
  );

  const skeletonText = "skeletonText";

  const displayLocationComponent =
    eventInfo?.eventLocationName &&
    eventInfo?.eventAddress &&
    eventInfo?.eventLatLng &&
    !eventInfo?.eventVirtualType;

  // Display informational text in the Location section
  const getLocationText = (): string => {
    let returnText = "";

    if (eventInfo?.eventVirtualType) {
      returnText = t("This event is online.");
    } else if (
      !eventInfo?.eventLocationName ||
      !eventInfo?.eventAddress ||
      !eventInfo?.eventLatLng
    ) {
      returnText = t("This venue hasn't specified a location for this event.");
    }

    return returnText;
  };

  const contactButtonInfo = [
    {
      id: 0,
      icon: (
        <Icon.User
          boxSize="element.2xl"
          mr={{ base: "sm", lg: "none" }}
          aria-label={t("profile icon")}
        />
      ),
      name: t("Organizer Profile"),
      handleClick: () => {
        router.push(`/o/${venueInfo.venueSlug}/`);
      },
      showButton: !isReactNativeApp && !isLoading,
      dataTestId: "organizer-profile-button",
    },
    {
      id: 1,
      icon: (
        <Icon.Info
          boxSize="element.2xl"
          mr={{ base: "sm", lg: "none" }}
          aria-label={t("help icon")}
        />
      ),
      name: t("Help & Info"),
      handleClick: () => {
        onOpen();
      },
      showButton: !isLoading,
      dataTestId: "help-&-info-button",
    },
    {
      id: 2,
      icon: (
        <Icon.Call
          boxSize="element.2xl"
          mr={{ base: "sm", lg: "none" }}
          aria-label={t("contact icon")}
        />
      ),
      name: t("Contact Us"),
      handleClick: () => {
        if (openContactDetailsModal) {
          openContactDetailsModal();
        }
      },
      showButton: !venueInfo.venuePrivate && !isLoading,
      dataTestId: "contact-us-button",
    },
  ];

  const visibleButtons = contactButtonInfo.filter(
    (filterButton) => filterButton.showButton
  );

  const scrollMargin = useDetailPageScrollMargin();

  const isCustomerReviewsEnabled = isFlagActive(
    "enable_customer_reviews",
    venueInfo.venueId
  );
  const reviewsData = useCustomerReviewsData({
    venueId: venueInfo.venueId,
    venueRating: venueInfo.venueRating,
    eventId: eventInfo?.eventId,
    eventRating: venueInfo.eventRating,
    enabled: isCustomerReviewsEnabled,
  });
  const showDistribution = isFlagActive(
    "show_review_rating_distribution",
    venueInfo.venueId
  );

  return (
    <>
      {isLoading && (
        <Flex direction="column" rowGap="xl" w="full">
          <DetailCardContainer>
            <Skeleton mb="xs">
              <Text fontSize={{ base: "md", sm: "lg" }}>{skeletonText}</Text>
            </Skeleton>

            <Skeleton height="element.4xl" />
          </DetailCardContainer>

          <DetailCardContainer>
            <Skeleton mb="xs">
              <Text fontSize={{ base: "md", sm: "lg" }}>{skeletonText}</Text>
            </Skeleton>

            <Skeleton mb="md">
              <Text fontSize={{ base: "0.75rem", sm: "0.875rem" }}>
                {skeletonText}
              </Text>
            </Skeleton>

            <Flex
              direction={{ base: "column", lg: "row" }}
              justifyContent="space-between"
              gap="xl"
            >
              {contactButtonInfo.map((button) => (
                <Skeleton
                  p="lg"
                  width="full"
                  height="90px"
                  key={button.id}
                  borderRadius="md"
                >
                  {button.name}
                </Skeleton>
              ))}
            </Flex>
          </DetailCardContainer>

          <DetailCardContainer>
            <Skeleton mb="xs">
              <Text fontSize={{ base: "md", sm: "lg" }}>{skeletonText}</Text>
            </Skeleton>
            <Skeleton height="element.4xl" />
          </DetailCardContainer>

          <DetailCardContainer>
            <Skeleton mb="xs">
              <Text fontSize={{ base: "md", sm: "lg" }}>{skeletonText}</Text>
            </Skeleton>
            <Skeleton height="300px" />
          </DetailCardContainer>
        </Flex>
      )}

      {!isLoading && (
        <Flex direction="column" w="full" rowGap="xl">
          <HelpAndInfoModalSelecton
            venueRefundPolicy={refundPolicyText}
            isOpen={isOpen}
            onClose={onClose}
          />
          {infoText && isStringWithContent(infoText) && (
            <DetailCardContainer data-testid="description-container">
              <Text
                mb="xs"
                fontSize={{ base: "md", sm: "lg" }}
                fontWeight="bold"
                color="fg"
              >
                {infoHeader}
              </Text>
              {infoText && <ParsedHtml htmlString={infoText} />}
            </DetailCardContainer>
          )}

          {infoRestrictions && infoRestrictions.length > 0 && (
            <DetailCardContainer data-testid="important-info-container">
              <Flex
                id="important-info"
                scrollMarginTop={scrollMargin}
                direction="column"
              >
                <Text
                  mb="xs"
                  fontSize={{ base: "md", sm: "lg" }}
                  fontWeight="bold"
                  color="fg"
                >
                  {t("Important Info")}
                </Text>
                <EventRestrictions restrictionArray={infoRestrictions} />
              </Flex>
            </DetailCardContainer>
          )}

          <ErrorBoundary fallback={null}>
            {eventInfo && (
              <DetailCardContainer data-testid="location-container">
                <Flex
                  id="location"
                  scrollMarginTop={scrollMargin}
                  direction="column"
                  w="full"
                  gap="xl"
                >
                  <Text
                    id="scrollToGoogleMap"
                    scrollMarginTop={scrollMargin}
                    fontSize={{ base: "md", sm: "lg" }}
                    fontWeight="bold"
                    color="fg"
                  >
                    {t("Location")}
                  </Text>

                  {!!getLocationText() && (
                    <Text
                      fontSize={{ base: "0.75rem", sm: "0.875rem" }}
                      color="fg.muted"
                    >
                      {getLocationText()}
                    </Text>
                  )}

                  {/* show the location and map if the address and latitude/longitude both exist */}
                  {/* show the location and map if the event isn't online */}
                  {displayLocationComponent && (
                    <Flex direction="column" w="full" gap="xl">
                      <Flex alignItems="flex-start" color="fg">
                        <Icon.Location
                          boxSize="element.xl"
                          aria-hidden="true"
                        />
                        <Flex
                          direction="column"
                          ml="sm"
                          fontSize={{ base: "0.75rem", sm: "0.875rem" }}
                          color="fg"
                        >
                          <Text>{eventInfo.eventLocationName}</Text>
                          <Text>{eventInfo.eventAddress}</Text>
                        </Flex>
                      </Flex>

                      <Flex height="340px" width="100%">
                        {eventInfo.viewMap ? (
                          <GoogleMapContainer
                            latLngPosition={eventInfo.eventLatLng}
                            displayMarker
                          />
                        ) : (
                          <Button
                            data-testid="detail-page-info-view-map"
                            fontWeight="default"
                            cursor="pointer"
                            height="340px"
                            width="100%"
                            borderRadius="md"
                            justifyContent="center"
                            alignItems="center"
                            bg="bg.muted"
                            color="fg"
                            _hover={{
                              _light: { bg: "gray.300" },
                              _dark: { bg: "whiteAlpha.400" },
                            }}
                            onClick={() => eventInfo.handleViewMap(true)}
                          >
                            <Text>{t("View Map")}</Text>
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              </DetailCardContainer>
            )}
          </ErrorBoundary>

          <ErrorBoundary fallback={null}>
            {reviewsData && (
              <DetailCardContainer data-testid="customer-reviews-container">
                <Flex
                  id="customer-reviews"
                  scrollMarginTop={scrollMargin}
                  direction="column"
                  w="full"
                  gap="xs"
                >
                  <Text
                    fontSize={{ base: "md", sm: "lg" }}
                    fontWeight="bold"
                    color="primary.600"
                  >
                    {t(CUSTOMER_REVIEWS.CUSTOMER_REVIEWS)}
                  </Text>

                  <CustomerReviews
                    overallRating={reviewsData.overallRating}
                    reviews={reviewsData.reviews}
                    isLoading={reviewsData.isLoading}
                    hasNoResults={reviewsData.hasNoResults}
                    hasNextPage={reviewsData.hasNextPage}
                    isFetchingNextPage={reviewsData.isFetchingNextPage}
                    onLoadMore={reviewsData.fetchNextPage}
                    activeStarRating={reviewsData.activeStarRating}
                    onStarRatingChange={reviewsData.setActiveStarRating}
                    showDistribution={showDistribution}
                  />
                </Flex>
              </DetailCardContainer>
            )}
          </ErrorBoundary>

          <ErrorBoundary fallback={null}>
            {eventInfo?.eventAccommodationUrl && (
              <Accommodations url={eventInfo?.eventAccommodationUrl} />
            )}
          </ErrorBoundary>

          <ErrorBoundary fallback={null}>
            <DetailCardContainer data-testid="contact-information-container">
              <Text
                mb="xs"
                fontSize={{ base: "md", sm: "lg" }}
                fontWeight="bold"
                color="fg"
              >
                {t("Contact Information")}
              </Text>

              {venueInfo.venueDescription ? (
                <Text
                  mb="md"
                  fontSize={{ base: "0.75rem", sm: "0.875rem" }}
                  color="fg.muted"
                >
                  {venueInfo.venueDescription}
                </Text>
              ) : (
                <></>
              )}

              <Flex
                direction={{ base: "column", lg: "row" }}
                justifyContent="space-between"
                gap="xl"
              >
                {visibleButtons.map((button) => (
                  <Button
                    key={button.id}
                    cursor="pointer"
                    display="flex"
                    flexDirection="column"
                    alignItems={{ base: "flex-start", lg: "center" }}
                    flexGrow={1}
                    h="full"
                    p="md"
                    fontSize="sm"
                    bg="bg.muted"
                    borderRadius="md"
                    fontWeight="default"
                    _hover={{
                      _light: { bg: "gray.400" },
                      _dark: { bg: "whiteAlpha.400" },
                    }}
                    onClick={() => button.handleClick()}
                    data-testid={button.dataTestId}
                  >
                    <Flex
                      direction={{ base: "row", lg: "column" }}
                      alignItems="center"
                      color="fg"
                    >
                      <span>{button.icon}</span>
                      <Text wordBreak="break-word" pt="2xs" color="primary.600">
                        {button.name}
                      </Text>
                    </Flex>
                  </Button>
                ))}
              </Flex>
            </DetailCardContainer>
          </ErrorBoundary>

          <DetailCardContainer data-testid="refund-policy-container">
            <Text
              mb="xs"
              fontSize={{ base: "md", sm: "lg" }}
              fontWeight="bold"
              color="fg"
            >
              {t("Refund Policy")}
            </Text>
            <Text
              fontSize={{ base: "0.75rem", sm: "0.875rem" }}
              color="fg.muted"
            >
              {refundPolicyText
                ? t(`${refundPolicyText}`)
                : t("This venue hasn't specified a refund policy.")}
            </Text>
          </DetailCardContainer>

          <ErrorBoundary fallback={null}>
            {upcomingEvents &&
              upcomingEvents?.length > 0 &&
              !isReactNativeApp && (
                <DetailCardContainer data-testid="more-events-purchase-container">
                  <Text
                    mb="md"
                    fontSize={{ base: "md", sm: "lg" }}
                    fontWeight="bold"
                    color="fg"
                  >
                    {t("More Events From This Organizer")}
                  </Text>

                  <UpcomingEventsSlider upcomingEvents={upcomingEvents} />
                </DetailCardContainer>
              )}
          </ErrorBoundary>
        </Flex>
      )}
    </>
  );
};

export default DetailPageInfo;
